import React, { useEffect, useState } from "react";
import { X, Package, Loader2, AlertCircle, Clock } from "lucide-react";
import { Button } from "../ui/button";
import { SearchableSelect } from "../SearchableSelect";
import { exchangeApi } from "../../api/exchangeApi";

export const CreateExchangeModal = ({
  isOpen,
  onClose,
  onCreateExchange,
  currentUserId,
  otherUserId,
  loading = false,
}) => {
  const [initiatorListingId, setInitiatorListingId] = useState("");
  const [recipientListingId, setRecipientListingId] = useState("");
  const [userListings, setUserListings] = useState([]);
  const [otherUserListings, setOtherUserListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [creating, setCreating] = useState(false);
  const [initiatorSelectOpen, setInitiatorSelectOpen] = useState(false);
  const [recipientSelectOpen, setRecipientSelectOpen] = useState(false);

  // Enhanced error handling state
  const [error, setError] = useState("");
  const [errorType, setErrorType] = useState(null);
  const [cooldownEndsAt, setCooldownEndsAt] = useState(null);

  useEffect(() => {
    if (isOpen && currentUserId && otherUserId) {
      loadListings();
      // Clear any previous errors when modal opens
      setError("");
      setErrorType(null);
      setCooldownEndsAt(null);
    }
  }, [currentUserId, isOpen, otherUserId]);

  // Clear error when user changes selections
  useEffect(() => {
    if (error) {
      setError("");
      setErrorType(null);
      setCooldownEndsAt(null);
    }
  }, [initiatorListingId, recipientListingId]);

  const loadListings = async () => {
    setLoadingListings(true);
    try {
      const userResponse = await exchangeApi.getCurrentUsersListings(
        currentUserId
      );
      console.log("userResponse", userResponse);

      const otherUserResponse = await exchangeApi.getOtherUserListings(
        otherUserId
      );
      console.log("otherUserResponse", otherUserResponse);

      if (userResponse.success) {
        setUserListings(userResponse.listings);
      }

      if (otherUserResponse.success) {
        setOtherUserListings(otherUserResponse.listings);
      }
    } catch (error) {
      console.error("Error loading listings:", error);
    } finally {
      setLoadingListings(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // Clear any previous errors
    setErrorType(null);
    setCooldownEndsAt(null);

    if (!initiatorListingId.trim() || !recipientListingId.trim()) {
      setError("Please select both listings to create an exchange.");
      return;
    }

    setCreating(true);
    try {
      await onCreateExchange(
        initiatorListingId.trim(),
        recipientListingId.trim()
      );
      setInitiatorListingId("");
      setRecipientListingId("");
      onClose();
    } catch (error) {
      console.error("Error creating exchange:", error);
      console.log("Full error response:", error.response?.data); // Debug log
      console.log("Error object:", error); // Debug log

      // Enhanced error handling with cooldown support
      let errorMessage = "Unable to create exchange. Please try again.";
      let errorReason = null;
      let cooldownTime = null;

      if (error.response?.data) {
        const responseData = error.data; // now your cooldown info will be accessible

        // Server returned a specific error message
        if (responseData.message) {
          errorMessage = responseData.message;
        }

        // Extract error type/reason
        if (responseData.error) {
          errorReason = responseData.error;
        }

        // Extract cooldown end time (check both possible fields)
        if (responseData.cooldownEndsAtFormatted) {
          cooldownTime = responseData.cooldownEndsAtFormatted;
        } else if (responseData.cooldownEndsAt) {
          // Fallback: format the date ourselves if only raw date is provided
          const cooldownDate = new Date(responseData.cooldownEndsAt);
          cooldownTime = cooldownDate.toLocaleString();
        }

        console.log("Extracted cooldown time:", cooldownTime); // Debug log
      } else if (error.message) {
        // Generic error message from API layer
        errorMessage = error.message;

        // Try to detect cooldown errors from message content
        if (
          error.message.includes("wait") &&
          (error.message.includes("days") || error.message.includes("day"))
        ) {
          errorReason = error.message.includes("7 days")
            ? "SAME_LISTINGS_COOLDOWN"
            : "DIFFERENT_LISTINGS_COOLDOWN";

          // Parse the error message to determine cooldown type
          if (error.message.includes("same exchange")) {
            errorReason = "SAME_LISTINGS_COOLDOWN";
          } else if (error.message.includes("another exchange")) {
            errorReason = "DIFFERENT_LISTINGS_COOLDOWN";
          } else if (error.message.includes("pending exchange")) {
            errorReason = "PENDING_EXCHANGE_EXISTS";
          }
        }
      }

      setError(errorMessage);
      setErrorType(errorReason);
      setCooldownEndsAt(cooldownTime);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    if (creating) return;
    setInitiatorListingId("");
    setRecipientListingId("");
    setError(""); // Clear errors when closing
    setErrorType(null);
    setCooldownEndsAt(null);
    onClose();
  };

  // Helper function to determine if error is cooldown-related
  const isCooldownError = () => {
    return (
      errorType &&
      (errorType === "SAME_LISTINGS_COOLDOWN" ||
        errorType === "DIFFERENT_LISTINGS_COOLDOWN" ||
        errorType === "PENDING_EXCHANGE_EXISTS")
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Package className="w-5 h-5 text-igbanwo-mint" />
            <h2 className="text-lg font-semibold text-gray-900">
              Create Exchange
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={creating}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 text-sm mb-6">
            Create an exchange request for a specific listing. Both parties will
            need to confirm the exchange.
          </p>

          {/* Enhanced Error Message Display */}
          {error && (
            <div
              className={`mb-6 p-4 border rounded-xl shadow-sm animate-fadeIn ${
                isCooldownError()
                  ? "bg-amber-50 border-amber-200"
                  : "bg-red-50 border-red-200"
              }`}
            >
              <div className="flex items-start space-x-3">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    isCooldownError() ? "bg-amber-100" : "bg-red-100"
                  }`}
                >
                  {isCooldownError() ? (
                    <Clock className="w-4 h-4 text-amber-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  )}
                </div>

                <div className="flex-1">
                  <h3
                    className={`text-base font-semibold ${
                      isCooldownError() ? "text-amber-800" : "text-red-800"
                    }`}
                  >
                    {isCooldownError()
                      ? "Exchange Temporarily Unavailable"
                      : "Unable to Create Exchange"}
                  </h3>

                  <p
                    className={`text-sm mt-1 leading-relaxed ${
                      isCooldownError() ? "text-amber-700" : "text-red-700"
                    }`}
                  >
                    {error}
                  </p>

                  {/* Cooldown End Time Display */}
                  {cooldownEndsAt && (
                    <div className="mt-3">
                      <span className="text-sm font-medium text-amber-800">
                        You can try again after: {" "}
                      </span>
                      <div className="mt-2 px-3 py-2 bg-amber-100 border border-amber-200 rounded-lg inline-block">
                        <p className="text-sm font-mono text-amber-800">
                          {cooldownEndsAt}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {loadingListings ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-igbanwo-mint" />
              <span className="ml-2 text-gray-600">Loading listings...</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Expandable container for selects */}
              <div className="relative">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Your Listing (Initiator) */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      What you're offering *
                    </label>

                    <div
                      className={`min-h-[${
                        initiatorSelectOpen ? "280px" : "0"
                      }] transition-all duration-300 flex items-start`}
                    >
                      <SearchableSelect
                        options={userListings}
                        value={initiatorListingId}
                        onValueChange={setInitiatorListingId}
                        placeholder="Select your listing"
                        searchPlaceholder="Search your listings..."
                        disabled={creating}
                        emptyMessage="You don't have any listings yet. Create a listing first."
                        formatOption={(listing) => listing.title}
                        getValue={(listing) => listing._id}
                        className="w-full"
                        onToggle={(open) => setInitiatorSelectOpen(open)}
                      />
                    </div>
                  </div>

                  {/* Their Listing (Recipient) */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      What they're offering *
                    </label>

                    <div
                      className={`min-h-[${
                        recipientSelectOpen ? "280px" : "0"
                      }] transition-all duration-300 flex items-start`}
                    >
                      <SearchableSelect
                        options={otherUserListings}
                        value={recipientListingId}
                        onValueChange={setRecipientListingId}
                        placeholder="Select their listing"
                        searchPlaceholder="Search their listings..."
                        disabled={creating}
                        emptyMessage="They don't have any listings available."
                        formatOption={(listing) => listing.title}
                        getValue={(listing) => listing._id}
                        className="w-full"
                        onToggle={(open) => setRecipientSelectOpen(open)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Exchange Preview */}
              {initiatorListingId && recipientListingId && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Exchange Preview:
                  </h3>
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center">
                      <span className="font-medium">You offer:</span>
                      <span className="ml-2">
                        {
                          userListings.find((l) => l._id === initiatorListingId)
                            ?.title
                        }
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      <span className="font-medium">They offer:</span>
                      <span className="ml-2">
                        {
                          otherUserListings.find(
                            (l) => l._id === recipientListingId
                          )?.title
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={creating}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    creating || !initiatorListingId || !recipientListingId
                  }
                  className="flex-1 bg-igbanwo-mint hover:bg-emerald-600"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Exchange"
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
